import { useMutation, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useRouteParams } from '@/contexts/RouteContext';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';
import { type VoucherDiscountTypeEnum } from '@/graphql/generated';

import { useVoucherDiscountTypeOptions } from './server-data-hooks';

const RESOURCE_NAME = 'vouchers';
import { VoucherForm, type VoucherFormSubmitData } from './voucher-form';
import { buildUpdateVoucherInput } from './voucher-input';
import { VOUCHER_QUERY, VOUCHER_UPDATE_MUTATION } from './vouchers.gql.tsx';

interface VoucherQueryResponse {
	voucher?: {
		voucherId: string;
		campaignId?: string | null;
		name?: string | null;
		code: string;
		isAdHoc: boolean;
		isCustomerSpecific: boolean;
		customerId?: string | null;
		rateBindingRequired: boolean;
		rateId?: string | null;
		voucherDiscountTypeEnum: VoucherDiscountTypeEnum;
		discountValue?: number | null;
		maxDiscountAmount?: number | null;
		freeTimeMinutes?: number | null;
		usageLimit?: number | null;
		isActive: boolean;
		validUntilDate: string;
		customer?: {
			customerId: string;
			firmName?: string | null;
			email?: string | null;
		} | null;
		rate?: {
			rateId: string;
			name: string;
		} | null;
	} | null;
}

const ULID_REGEX = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/;

export const VouchersEdit = () => {
	const { t } = useTranslation();
	const { id } = useRouteParams();
	const go = useGo();
	useEditPageHeader({ id, resourceName: RESOURCE_NAME });

	const isValidUlid = useMemo(() => {
		return typeof id === 'string' && ULID_REGEX.test(id.toUpperCase());
	}, [id]);

	const { voucherDiscountTypeOptions } = useVoucherDiscountTypeOptions();

	const { data, loading, error, refetch } = useQuery<VoucherQueryResponse>(
		VOUCHER_QUERY,
		{
			variables: { voucherId: id },
			skip: !id || !isValidUlid
		}
	);

	const voucherRecord = data?.voucher;

	const initialValues = useMemo<VoucherFormSubmitData | null>(() => {
		if (!voucherRecord) {
			return null;
		}

		return {
			voucherId: voucherRecord.voucherId,
			campaignId: voucherRecord.campaignId ?? null,
			name: voucherRecord.name ?? null,
			code: voucherRecord.code,
			isAdHoc: voucherRecord.isAdHoc,
			isCustomerSpecific: voucherRecord.isCustomerSpecific,
			customerId: voucherRecord.customerId ?? null,
			rateBindingRequired: voucherRecord.rateBindingRequired,
			rateId: voucherRecord.rateId ?? null,
			voucherDiscountTypeEnum: voucherRecord.voucherDiscountTypeEnum,
			discountValue: voucherRecord.discountValue ?? null,
			maxDiscountAmount: voucherRecord.maxDiscountAmount ?? null,
			freeTimeMinutes: voucherRecord.freeTimeMinutes ?? null,
			usageLimit: voucherRecord.usageLimit ?? null,
			isActive: voucherRecord.isActive,
			validUntilDate: voucherRecord.validUntilDate,
			customer: voucherRecord.customer ?? null
		};
	}, [voucherRecord]);

	const [updateVoucher, { loading: isSubmitting }] = useMutation(
		VOUCHER_UPDATE_MUTATION
	);

	const handleSubmit = async (values: VoucherFormSubmitData) => {
		if (!initialValues?.voucherId) {
			showMutationErrorToast('vouchers', 'update', { message: 'Voucher identifier is missing.' });
			return;
		}

		const payload = buildUpdateVoucherInput(
			values,
			initialValues.voucherId
		);

		try {
			const { data: mutationData } = await updateVoucher(
				getMutationOptions('vouchers', { input: payload })
			);

			const message = extractCheckResultError(
				mutationData?.updateVoucher
			);
			if (message) {
				showMutationErrorToast('vouchers', 'update', { message });
				return;
			}

			showMutationSuccessToast('vouchers', 'update');
			// Navigate back with the edited voucher ID for selection
			go({
				to: '/vouchers',
				state: { selectVoucherId: initialValues.voucherId }
			});
		} catch (error) {
			showMutationErrorToast('vouchers', 'update', { error });
		}
	};

	if (!id) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>{t('vouchers:messages.notFound')}</AlertTitle>
					<AlertDescription>
						{t('vouchers:messages.invalidId')}
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (!isValidUlid) {
		return (
			<div className="">
				<Card className="space-y-4">
					<h2 className="text-lg font-semibold text-destructive">
						Invalid voucher identifier
					</h2>
					<p className="text-sm text-muted-foreground">
						The provided voucher id{' '}
						<code className="px-2 py-1 rounded bg-muted">{id}</code>{' '}
						is not a valid ULID value.
					</p>
					<Button
						variant="outline"
						onClick={() => go({ to: '/vouchers' })}
					>
						Back to vouchers
					</Button>
				</Card>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-3">
					<Button
						variant="outline"
						onClick={() => go({ to: '/vouchers' })}
					>
						← Back
					</Button>
					<h1 className="text-2xl font-semibold">Edit voucher</h1>
				</div>
				<Card className="text-sm text-muted-foreground">
					Loading voucher details...
				</Card>
			</div>
		);
	}

	if (error || !initialValues) {
		return (
			<div className="p-4">
				<Alert variant="destructive">
					<AlertTitle>Unable to load voucher</AlertTitle>
					<AlertDescription>
						{error?.message ??
							'The requested voucher could not be found.'}
					</AlertDescription>
				</Alert>
				<div className="mt-4">
					<Button variant="outline" onClick={() => void refetch()}>
						Retry
					</Button>
				</div>
			</div>
		);
	}

	return (
		<FormPageLayout>
			<VoucherForm
				key={`edit-voucher-${initialValues?.voucherId || 'unknown'}`}
				mode="edit"
				initialData={initialValues}
				voucherDiscountTypeOptions={voucherDiscountTypeOptions}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle={t('vouchers:edit.title')}
			/>
		</FormPageLayout>
	);
};
